import CryptoJS from 'crypto-js';

import { User } from 'types';
import { Config } from 'config';

const userKey = 'authUser';
const darkThemeKey = 'isDarkTheme';
const rememberedEmailKey = 'rememberedEmail';
const externalPaymentIdKey = 'externalPaymentId';
const selectedCloverDevicesKey = 'selectedCloverDevices';

type ExternalPaymentIds = Record<string, string>;

export interface Tokens {
  token?: string;
  refreshToken?: string;
}

export type TokenKey = 'accessToken' | 'refreshToken';

const accessTokenKey: TokenKey = 'accessToken';
const refreshTokenKey: TokenKey = 'refreshToken';
const tokenKeys: TokenKey[] = [accessTokenKey, refreshTokenKey];

export const rememberEmail = (remember: boolean, email?: string) => {
  if (remember && email) {
    localStorage.setItem(rememberedEmailKey, email);
  } else {
    localStorage.removeItem(rememberedEmailKey);
  }
};

export const getRememberedEmail = () =>
  localStorage.getItem(rememberedEmailKey);

export const saveDarkTheme = (isDarkTheme?: boolean) => {
  if (typeof isDarkTheme === 'boolean') {
    localStorage.setItem(darkThemeKey, String(isDarkTheme));
  } else {
    localStorage.removeItem(darkThemeKey);
  }
};

export const getIsDarkTheme = (): boolean | undefined => {
  const isDarkTheme = localStorage.getItem(darkThemeKey);
  if (isDarkTheme === null) {
    return undefined;
  }
  return isDarkTheme === 'true';
};

export const getSelectedCloverDevices = (): Record<number, number> => {
  try {
    const stored = localStorage.getItem(selectedCloverDevicesKey);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export const saveSelectedCloverDevices = (devices: Record<number, number>) => {
  localStorage.setItem(selectedCloverDevicesKey, JSON.stringify(devices));
};

export const clearAllSelectedCloverDevices = () =>
  localStorage.removeItem(selectedCloverDevicesKey);

export const saveExternalPaymentId = (eventId: string, paymentId: string) => {
  const existing = localStorage.getItem(externalPaymentIdKey);
  const parsed: ExternalPaymentIds = existing ? JSON.parse(existing) : {};

  parsed[eventId] = paymentId;

  localStorage.setItem(externalPaymentIdKey, JSON.stringify(parsed));
};

export const getExternalPaymentId = (eventId: string) => {
  const existing = localStorage.getItem(externalPaymentIdKey);
  if (!existing) {
    return;
  }

  const parsed: ExternalPaymentIds = JSON.parse(existing);
  return parsed[eventId];
};

export const clearExternalPaymentId = (eventId?: string) => {
  if (!eventId) {
    localStorage.removeItem(externalPaymentIdKey);
    return;
  }

  const existing = localStorage.getItem(externalPaymentIdKey);
  if (!existing) {
    return;
  }

  const parsed: ExternalPaymentIds = JSON.parse(existing);
  delete parsed[eventId];

  if (Object.keys(parsed).length === 0) {
    localStorage.removeItem(externalPaymentIdKey);
  } else {
    localStorage.setItem(externalPaymentIdKey, JSON.stringify(parsed));
  }
};

export const clearTokens = () =>
  tokenKeys.forEach((tokenKey) => localStorage.removeItem(tokenKey));

export const clearUser = () => localStorage.removeItem(userKey);

export const clearAll = () => {
  clearTokens();
  clearUser();
  clearExternalPaymentId();
  clearAllSelectedCloverDevices();
};

export const saveToken = (tokenKey: TokenKey, token: string) =>
  localStorage.setItem(
    tokenKey,
    CryptoJS.AES.encrypt(token, Config.cryptoKey).toString()
  );

export const saveTokens = ({ token, refreshToken }: Tokens) => {
  if (token) {
    saveToken('accessToken', token);
  }
  if (refreshToken) {
    saveToken('refreshToken', refreshToken);
  }
};

export const getToken = (tokenKey: TokenKey = accessTokenKey) => {
  const token = localStorage.getItem(tokenKey);

  if (!token) {
    return;
  }

  try {
    return CryptoJS.AES.decrypt(token, Config.cryptoKey).toString(
      CryptoJS.enc.Utf8
    );
  } catch {
    clearAll();
  }
};

export const decryptToken = (token: string) =>
  CryptoJS.AES.decrypt(token, Config.cryptoKey).toString(CryptoJS.enc.Utf8);

export const saveUser = (user: User) =>
  localStorage.setItem(userKey, JSON.stringify(user));

export const getUser = (): User | undefined => {
  const user = localStorage.getItem(userKey);
  if (!user) {
    return undefined;
  }
  return JSON.parse(user);
};

export const getTokens = () => ({
  token: getToken('accessToken'),
  refreshToken: getToken('refreshToken'),
});
